import { Component, Input, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ViewTableComponent } from 'src/app/components/base/view-table/view-table.component';
import { EventLessonEntityComponent } from 'src/app/components/forms/event-lesson-entity/event-lesson-entity.component';
import { PrintService } from 'src/app/services/print.service';

@Component({
  selector: 'app-student-future-lessons',
  templateUrl: './student-future-lessons.component.html',
  styleUrls: ['./student-future-lessons.component.css']
})
export class StudentFutureLessonsComponent extends ViewTableComponent implements OnInit {

  @Input() drupal_student_id: number;
  @Input() drupal_student_name: string;
  studentFutureLessonsData;
  override pageSize: number = 5;
  private _printService: PrintService;

  // Frontend safeguard against accidental duplicate sends: the Email button
  // disables itself once a send is in flight / has completed for this view.
  scheduleEmailSending: boolean = false;
  scheduleEmailSent: boolean = false;

  override ngOnInit() {
    super.ngOnInit();
    this._printService = new PrintService(this._drupalRESTService, this._authService);
  }

  override displayedColumns: string[] = [
    'date',
    'time',
    'eventType',
    'instructorName',
    'lessonCategory',
    'enrollmentCategory',
    'edit'
  ];

  printUpcomingLessons() {
    const rows: any[] = this.dataSource?.data ?? [];

    const escape = (v: any) => {
      const s = v === null || v === undefined ? '' : String(v);
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    };

    const reportTitle = this.drupal_student_name
      ? `Upcoming Lessons: ${this.drupal_student_name}`
      : 'Upcoming Lessons';

    // Enrollment Category is intentionally omitted from the printed/PDF
    // schedule (kept on the on-screen table only).
    const headers = [
      'Date', 'Time', 'Event Type', 'Instructor Name', 'Lesson Category'
    ];

    const headerHtml = headers.map(h => `<th>${escape(h)}</th>`).join('');

    const bodyHtml = rows.map((r: any) => {
      return `<tr>
        <td class="nowrap">${escape(this.reportDateService.formatDate(r.field_date_and_time))}</td>
        <td class="nowrap">${escape(r.field_date_and_time_1)}</td>
        <td>${escape(r.event_type)}</td>
        <td>${escape(r.instructor)}</td>
        <td>${escape(r.enrollment_title)}</td>
      </tr>`;
    }).join('');

    const countHtml = `<div class="row-count">${rows.length} lesson${rows.length === 1 ? '' : 's'}</div>`;

    const content = `
      <h1 class="report-title">${escape(reportTitle)}</h1>
      ${countHtml}
      <table class="report-table">
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${bodyHtml}</tbody>
      </table>
    `;

    const styles = `
      @page { size: letter portrait; margin: 0.5in; }
      .report-table { width: 100%; border-collapse: collapse; font-size: 11px; }
      .report-table th, .report-table td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
      .report-table th { background: #f0f0f0; }
      .report-table td.nowrap { white-space: nowrap; }
      .row-count { margin: 4px 0 8px 0; font-size: 11px; color: #555; }
    `;

    this._printService.print(content, {
      title: reportTitle,
      styles,
      showSchoolName: true,
      promptPrint: true
    });
  }

  // Email the student their upcoming schedule (next 30 days) as a single email.
  // Reuses the backend lesson-reminder composer so opt-in, the studio template
  // and the notifications audit log all behave like a normal reminder. The
  // button disables once clicked to guard against accidental duplicate sends.
  emailUpcomingSchedule() {
    if (this.scheduleEmailSending || this.scheduleEmailSent) return;
    if (!this.drupal_student_id) return;

    this.scheduleEmailSending = true;

    this._drupalRESTService.httpGET(
      `/api_rest/v1/sendStudentScheduleEmail?contactID=${this.drupal_student_id}&daysAhead=30`
    ).subscribe(
      (data: any) => {
        this.scheduleEmailSending = false;

        if (data?.['error']) {
          this._snackBar.open(data['error'], 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            panelClass: ['error-snackbar'],
          });
          return;
        }

        if (data?.['warning']) {
          this._snackBar.open(data['warning'], 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
          });
          return;
        }

        if (data?.['skipped']) {
          this._snackBar.open(data['skipped'], 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
          });
          return;
        }

        // Success: lock the button so it can't be clicked again in this view.
        this.scheduleEmailSent = true;
        this._snackBar.open(
          data?.['message'] || 'Schedule emailed to the student.',
          'Close',
          { duration: 3000, horizontalPosition: 'center' }
        );
      },
      error => {
        this.scheduleEmailSending = false;
        this._snackBar.open('An error occurred while sending the schedule email.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          panelClass: ['error-snackbar'],
        });
      }
    );
  }

  // Method to open the correct edit component for the event's bundle.
  // The list now mixes lessons, group lessons and services, so each row routes
  // to its own entity form (matching the appointment-details edit pattern).
  openLessonEditComponent(lessonId: string, bundle: string = 'lesson'): void {
    if (!lessonId) return;

    const editComponentByBundle = {
      lesson: this.EventLessonEntityComponent,
      group_lesson: this.EventGroupLessonEntityComponent,
      services: this.EventServicesEntityComponent,
    };
    const component = editComponentByBundle[bundle] ?? this.EventLessonEntityComponent;

    const dialogRef = this._dialogService.openDialog(component, "defaultWithData", {
      data: {
        EntityID: lessonId,
        eckType: 'events',
        bundle: bundle ?? 'lesson',
        action: 'edit'
      },
    }).afterClosed().subscribe(data => {
      // Refresh the data after dialog is closed
      this.getData();
    });
  }

  override getData(): void;
  override getData(params: { parameter: string; value: string; }[]): void;
  override getData(params: { parameter?: string; value?: string; }[] = []) {

    // Show Progress Bar (Loading indicator)
    this.ShowProgressBar = true;

    // Initialize queryParams if params are not provided
    this.queryParams = [];

    // Add sorting paramaters, if available.
    if (this.sortParams !== undefined) {
      this.queryParams.push(this.sortParams)
    }

    const endpoint = `/api_rest/v1/studentFutureLessons?contact_id=${this.drupal_student_id}&items_per_page=${this.pageSize}`;

    this.studentFutureLessonsData = this._drupalRESTService.httpGET(endpoint);

    // Load the new data with the filtering parameters.
    this.studentFutureLessonsData.subscribe((data) => {
        // ShowProgressBar
        this.ShowProgressBar = false;

        // The endpoint returns retired lessons (Cancelled / Rescheduled /
        // No Showed Not Charged / Canceled Too Late) with is_retired=true
        // so the response stays honest; the table hides them to match
        // the day-view tooltip and appointment-details panel.
        const rawResults = Array.isArray(data?.['results']) ? data['results'] : [];
        const visibleResults = rawResults.filter((row: any) => !row?.is_retired);

        this.data = data;
        this.dataSource = new MatTableDataSource(visibleResults);

        // Pagination: count off visible rows so the page footer matches
        // what staff see, not the raw API count which includes retired.
        if (this.paginator?.['length']) {
          this.paginator.length = visibleResults.length;
          this.paginator.pageSize = this?.['pageSize'];
          this.paginator.pageIndex = this?.['paginationIndex'];
        }
      },
        error => this.handleError(error)
      )
  }



}
